/*
  We need to create and update the Cart Storage when ever the user adds/deletes
  items from the cart. The only way possible for this to occur is when a user goes
  to CART/CHECKOUT.

  We also need to check/create this if a USER logs in

  Artworks can sell behind the scenes and the cart could be cleaned up server side.
*/

import { get as getStorage, set as setStorage } from 'lib/localStorage'
import { fetchCart } from 'apis/cart'
import { CartStorage } from 'types/API/Easel/Cart'

export type CartStorageData = Partial<CartStorage> & {
  futureExpirationUnixTimestamp: number
  shouldSentDataLayerEvent: boolean
  wasAuthenticated: boolean
}

const STORAGE_KEY = 'saatchi_cart_data'
const SAATCHI_CLIENT_DATA_KEY = 'saatchi_client_data'
const DIGIOH_CART_CATEGORIES = 'sa_cart_categories'
const DIGIOH_CART_COUNT = 'sa_cart_count'
const DIGIOH_CART_ORIGINAL_TOTAL = 'sa_cart_orig_total_val'
const DIGIOH_CART_PRICE_AVERAGE = 'sa_cart_price_avg'
const EXPIRATION = 1000 * 60 * 60 * 15 // ms * s * m * h => 15 hours

const createDigiohStorage = (data: CartStorageData): void => {
  const {
    cartCategories = [],
    cartCount = 0,
    cartOriginalsTotalValue = 0,
    cartItemsPriceAverage = 0,
  } = data
  setStorage(DIGIOH_CART_CATEGORIES, cartCategories.join() || '')
  setStorage(DIGIOH_CART_COUNT, cartCount || 0)
  setStorage(DIGIOH_CART_ORIGINAL_TOTAL, parseInt(`${cartOriginalsTotalValue / 100}`, 10) || 0)
  setStorage(DIGIOH_CART_PRICE_AVERAGE, parseInt(`${cartItemsPriceAverage / 100}`, 10) || 0)
}

const killDigiohStorage = (): void => {
  localStorage.removeItem(DIGIOH_CART_CATEGORIES)
  localStorage.removeItem(DIGIOH_CART_COUNT)
  localStorage.removeItem(DIGIOH_CART_ORIGINAL_TOTAL)
  localStorage.removeItem(DIGIOH_CART_PRICE_AVERAGE)
}

export const getCartStorage = (): CartStorageData => getStorage(STORAGE_KEY) || {}

export const getIsUserAuthenticated = (): boolean => {
  const { session } = getStorage(SAATCHI_CLIENT_DATA_KEY) || {}

  return session?.isAuthenticated
}

export const setCartStorage = (data: Partial<CartStorageData>): void => {
  const cartData = getCartStorage()
  const updatedData = {
    ...cartData,
    ...data,
  }
  setStorage(STORAGE_KEY, updatedData)
  createDigiohStorage(updatedData)
}

export const deleteCartStorage = (): void => {
  localStorage.removeItem(STORAGE_KEY)
  killDigiohStorage()
}

export const setCartData = ({ cartStorage }: { cartStorage: CartStorage }): void => {
  const { futureExpirationUnixTimestamp: storageStamp, shouldSentDataLayerEvent } =
    getStorage(STORAGE_KEY) || {}
  // If exist get timestamp or lets crate a FUTURE timestamp
  const now = new Date().getTime()
  const isTimerActive = storageStamp > now
  const futureExpirationUnixTimestamp =
    !!storageStamp && isTimerActive ? storageStamp : now + EXPIRATION
  const dataLayerFlag = shouldSentDataLayerEvent === false ? false : true
  if (cartStorage.hasOriginal) {
    setCartStorage({
      ...cartStorage,
      futureExpirationUnixTimestamp,
      shouldSentDataLayerEvent: dataLayerFlag,
      wasAuthenticated: getIsUserAuthenticated(),
    })
  } else {
    deleteCartStorage()
  }
}

export const getCartData = async (): Promise<void> => {
  const { payload, success } = await fetchCart()
  if (success) {
    setCartData(payload)
  }
}

export const showNotification = (): boolean => {
  const { futureExpirationUnixTimestamp, hasOriginal } = getStorage(STORAGE_KEY) || {}
  // User does NOT have any cartData or a timestamp
  if (!futureExpirationUnixTimestamp || !hasOriginal) {
    return false
  }
  const now = new Date().getTime()
  // The time to show urgency has NOT occurred
  const show = futureExpirationUnixTimestamp < now
  return show
}

export const resetNotification = (): void => {
  const futureExpirationUnixTimestamp = new Date().getTime() + EXPIRATION
  setCartStorage({ futureExpirationUnixTimestamp, shouldSentDataLayerEvent: true })
}

export const sendCartUrgencyDataLayerEvent = () => {
  const { futureExpirationUnixTimestamp, hasOriginal, shouldSentDataLayerEvent } =
    getStorage(STORAGE_KEY) || {}

  if (!futureExpirationUnixTimestamp || !hasOriginal) {
    return
  }

  if (showNotification() && shouldSentDataLayerEvent) {
    if (window && window.dataLayer) {
      window.dataLayer.push({
        event: 'cart_urgency_flash_impression',
      })
      setCartStorage({ shouldSentDataLayerEvent: false })
    }
  }
}

export const checkExpiredLogin = (): void => {
  const { wasAuthenticated } = getStorage(STORAGE_KEY) || {}
  if (wasAuthenticated) {
    if (!getIsUserAuthenticated()) {
      deleteCartStorage()
    }
  } else {
    return
  }
}
